import {Inject, Injectable} from '@angular/core';
import {WINDOW} from '@ng-web-apis/common';
import {DOCUMENT} from '@angular/common';
import {ConsentFormValues} from '@ui/consent-banner/consent-banner.component';
import {Store} from '@ngxs/store';
import {AuthState} from '@store/auth/auth.state';

interface GtagWindow extends Window {
  dataLayer: unknown[];
  gtag: any;
}

@Injectable({providedIn: 'root'})
export class GoogleTagService {
  private gTagId: string | undefined;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(WINDOW) private readonly window: GtagWindow,
    private readonly store: Store,
  ) {}

  declareGtag() {
    this.window.dataLayer = this.window.dataLayer || [];
    this.window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      (window as any).dataLayer.push(arguments);
    };
  }

  addGtagScript(gtagId: string): void {
    this.gTagId = gtagId;
    const script = this.document.createElement('script');

    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gtagId}`;

    script.onload = () => {
      this.setGtm(gtagId);
    };

    script.onerror = () => {
      console.log('gtag script error');
    };

    this.document.head.insertBefore(script, this.document.head.firstChild);
  }

  setDefaultConsentGtag(): void {
    this.window.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    });
  }

  async setGtm(gtagId: string): Promise<void> {
    const userId = await this.hashUserId(
      this.store.selectSnapshot(AuthState.decodedToken)?.['custom:user_id'],
    );

    this.window.gtag('js', new Date());
    this.window.gtag('config', gtagId, {
      user_id: userId || null,
    });
  }

  // add_storage   Enables storage, such as cookies (web) or device identifiers (apps), related to advertising.
  // ad_user_data	Sets consent for sending user data to Google for online advertising purposes.
  // ad_personalization	Sets consent for personalized advertising.
  // analytics_storage	Enables storage, such as cookies (web) or device identifiers (apps), related to analytics, for example, visit duration.

  updateConsentGtag(consent: ConsentFormValues): void {
    if (!this.window.gtag) {
      return;
    }
    this.window.gtag('consent', 'update', {
      ['ad_storage']: consent.googleMarketingAdStorage ? 'granted' : 'denied',
      ['ad_user_data']: consent.googleMarketingAdUserData ? 'granted' : 'denied',
      ['ad_personalization']: consent.googleMarketingPersonalization
        ? 'granted'
        : 'denied',
      ['analytics_storage']: consent.googleAnalytics ? 'granted' : 'denied',
    });
  }

  async pushUserId(userId: string) {
    if (!this.window.gtag) {
      return;
    }

    this.window.gtag('config', this.gTagId, {
      user_id: await this.hashUserId(userId),
    });
  }

  resetUserId() {
    if (!this.window.gtag) {
      return;
    }
    this.window.gtag('config', this.gTagId, {
      user_id: null,
    });
  }

  pushEvent(eventName: string, data: any) {
    if (!this.window.gtag) {
      return;
    }
    this.window.gtag('event', eventName, data);
  }

  async hashUserId(userId: string | undefined) {
    if (!userId) {
      return;
    }
    const msgUint8 = new TextEncoder().encode(userId); // encode as (utf-8) Uint8Array
    const hashBuffer = await this.window.crypto.subtle.digest('SHA-256', msgUint8); // hash the message
    const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string

    return hashHex;
  }
}
